type QrCallback = (text: string) => boolean | void;

type TelegramBottomButton = {
    setText(text: string): TelegramBottomButton;
    onClick(callback: () => void): TelegramBottomButton;
    offClick(callback: () => void): TelegramBottomButton;
    show(): TelegramBottomButton;
    hide(): TelegramBottomButton;
    enable(): TelegramBottomButton;
    disable(): TelegramBottomButton;
    showProgress(leaveActive?: boolean): TelegramBottomButton;
    hideProgress(): TelegramBottomButton;
};

export type TelegramWebApp = {
    initData: string;
    initDataUnsafe: { start_param?: string; user?: Record<string, unknown> };
    colorScheme: 'light' | 'dark';
    platform?: string;
    isFullscreen?: boolean;
    ready(): void;
    expand(): void;
    /** Closes the Mini App; the next launch brings fresh init data. */
    close?(): void;
    requestFullscreen?(): void;
    exitFullscreen?(): void;
    requestWriteAccess?(callback?: (accepted: boolean) => void): void;
    requestContact(callback?: (accepted: boolean) => void): void;
    showScanQrPopup(params: { text?: string }, callback?: QrCallback): void;
    closeScanQrPopup(): void;
    enableClosingConfirmation(): void;
    disableClosingConfirmation(): void;
    setHeaderColor(color: string): void;
    setBackgroundColor?(color: string): void;
    MainButton?: TelegramBottomButton;
    /** Opens a `t.me` link inside Telegram — the share sheet for «Запросити друга». */
    openTelegramLink?(url: string): void;
    /** Opens a web page in Telegram's own browser — the payment page (2026-10-27). */
    openLink?(url: string, options?: { try_instant_view?: boolean }): void;
    HapticFeedback?: {
        impactOccurred(
            style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft',
        ): void;
        notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    };
};

declare global {
    interface Window {
        Telegram?: { WebApp?: TelegramWebApp };
    }
}

export function requestDeeplinkWriteAccess(app: TelegramWebApp | null): void {
    const startParam = app?.initDataUnsafe.start_param;
    const user = app?.initDataUnsafe.user;

    if (
        !startParam ||
        user?.allows_write_to_pm === true ||
        !app?.requestWriteAccess
    ) {
        return;
    }

    try {
        app.requestWriteAccess();
    } catch {
        // Older clients keep the Mini App usable without bot messages.
    }
}

export function telegram(): TelegramWebApp | null {
    return window.Telegram?.WebApp ?? null;
}

export function prepareTelegram(): TelegramWebApp | null {
    const app = telegram();

    app?.ready();

    if (!app) {
        return null;
    }

    const mobile = ['android', 'android_x', 'ios'].includes(app.platform ?? '');

    if (!mobile) {
        if (app.isFullscreen && app.exitFullscreen) {
            try {
                app.exitFullscreen();
            } catch {
                // A desktop client without exit support keeps its launch size.
            }
        }

        return app;
    }

    app.expand();

    if (app.requestFullscreen && !app.isFullscreen) {
        try {
            app.requestFullscreen();
        } catch {
            // Older or partially updated Telegram clients keep the expanded sheet.
        }
    }

    return app;
}
